import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Link2, LinkIcon, LogOut, RefreshCw, Search, TrendingUp, TrendingDown,
  AlertTriangle, Loader2, ChevronRight, Zap, Target, Layers, Copy,
  Brain, Sparkles, CheckCircle2, Info,
} from "lucide-react";
import { useAdminT } from "@/contexts/AdminLanguageContext";

/**
 * Prompt 17 — GSC dashboard, opportunity engine, and AI Advisor.
 * Sits inside AiSeoAdmin as a top-level tab. Uses the parent `onSendToMode`
 * callback to hand a query/URL back to the existing Improve or Create flow.
 */

const RANGES = [
  { key: "7d", labelKey: "gsc.range.7d" },
  { key: "28d", labelKey: "gsc.range.28d" },
  { key: "3m", labelKey: "gsc.range.3m" },
  { key: "6m", labelKey: "gsc.range.6m" },
  { key: "12m", labelKey: "gsc.range.12m" },
];

const TIER_COLOR = {
  VERY_HIGH: "text-[#ff9a3c] border-[#ff9a3c]",
  HIGH: "text-[#66d18a] border-[#66d18a]",
  MEDIUM: "text-[#C9A55A] border-[#C9A55A]",
  LOW: "text-[#8A8A8A] border-[#8A8A8A]",
};

function Kpi({ label, value, delta, suffix, invert = false, unit = "%" }) {
  const { t } = useAdminT();
  const positive = delta == null ? null : (invert ? delta < 0 : delta > 0);
  const arrow = delta == null ? null : positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />;
  const color = delta == null ? "text-[#8A8A8A]" : positive ? "text-[#66d18a]" : "text-[#e57373]";
  const shownDelta =
    delta == null ? "" :
    unit === "pp" ? `${delta >= 0 ? "+" : ""}${delta.toFixed(2)} pp` :
    unit === "pos" ? `${delta >= 0 ? "+" : ""}${delta.toFixed(2)} pos` :
    `${delta >= 0 ? "+" : ""}${delta.toFixed(2)}%`;
  return (
    <div className="border border-[#2A2A2A] p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">{label}</div>
      <div className="mt-2 text-2xl text-[#F5F1EB]">{value}{suffix || ""}</div>
      {delta != null && (
        <div className={`mt-1 inline-flex items-center gap-1 text-[11px] ${color}`}>
          {arrow} {shownDelta} {t("gsc.kpi.delta")}
        </div>
      )}
    </div>
  );
}

function SortHeader({ label, k, sort, onSort }) {
  const active = sort.key === k;
  return (
    <th
      onClick={() => onSort(k)}
      className={`px-2 py-1.5 text-[10px] uppercase tracking-[0.16em] cursor-pointer select-none ${active ? "text-[#C9A55A]" : "text-[#8A8A8A] hover:text-[#F5F1EB]"}`}
    >
      {label}{active && (sort.dir === "asc" ? " ↑" : " ↓")}
    </th>
  );
}

function useSortedRows(rows, initial = { key: "clicks", dir: "desc" }) {
  const [sort, setSort] = useState(initial);
  const onSort = (k) => setSort((s) => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "desc" });
  const sorted = useMemo(() => {
    const copy = [...(rows || [])];
    copy.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number") return sort.dir === "asc" ? av - bv : bv - av;
      return sort.dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [rows, sort]);
  return { sorted, sort, onSort };
}

function EmptyGsc({ onConnect, configured, redirectUri }) {
  const { t } = useAdminT();
  return (
    <div className="border border-dashed border-[#2A2A2A] p-8 text-center" data-testid="gsc-empty-state">
      <Search size={28} className="mx-auto text-[#C9A55A] mb-3" />
      <h3 className="font-display text-xl text-[#F5F1EB] mb-2">{t("gsc.empty_headline")}</h3>
      <p className="text-sm text-[#8A8A8A] max-w-lg mx-auto mb-5">
        {t("gsc.empty_body")}
      </p>
      {configured ? (
        <button onClick={onConnect} className="btn-gold" data-testid="gsc-connect-btn">
          <Link2 size={14} className="inline mr-1" /> {t("gsc.connect_btn")}
        </button>
      ) : (
        <div className="text-left max-w-2xl mx-auto text-xs text-[#F5F1EB] border border-[#4a3315] bg-[#1a0d0d] p-4">
          <div className="text-[#C9A55A] uppercase tracking-[0.18em] mb-2 flex items-center gap-2">
            <AlertTriangle size={12} /> {t("gsc.setup_required")}
          </div>
          <p>The server does not yet have Google OAuth credentials. Follow the setup guide (below the panel) to create them in Google Cloud Console, then paste them into <code className="text-[#C9A55A]">backend/.env</code>:</p>
          <pre className="bg-[#0C0C0C] p-2 mt-2 text-[11px] overflow-x-auto">{`GOOGLE_CLIENT_ID="..."
GOOGLE_CLIENT_SECRET="..."`}</pre>
          {redirectUri && (
            <p className="mt-2">Authorized redirect URI: <code className="text-[#C9A55A]">{redirectUri}</code></p>
          )}
        </div>
      )}
    </div>
  );
}

function QueriesTable({ rows, onSendToImprove }) {
  const { t } = useAdminT();
  const [q, setQ] = useState("");
  const filtered = useMemo(() =>
    (rows || []).filter((r) => !q || r.query.toLowerCase().includes(q.toLowerCase())),
    [rows, q]);
  const { sorted, sort, onSort } = useSortedRows(filtered);
  return (
    <div className="border border-[#2A2A2A]">
      <div className="p-3 border-b border-[#2A2A2A] flex items-center gap-2">
        <Search size={14} className="text-[#8A8A8A]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("gsc.table.filter_queries")}
          className="bg-transparent border-none outline-none text-sm text-[#F5F1EB] flex-1"
          data-testid="gsc-queries-search"
        />
      </div>
      <table className="w-full">
        <thead className="border-b border-[#2A2A2A]">
          <tr className="text-left">
            <SortHeader label={t("gsc.table.query")} k="query" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.clicks")} k="clicks" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.impressions")} k="impressions" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.ctr")} k="ctr" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.position")} k="position" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.trend")} k="trend" sort={sort} onSort={onSort} />
            <th />
          </tr>
        </thead>
        <tbody className="text-sm">
          {sorted.slice(0, 100).map((r) => (
            <tr key={r.query} className="border-b border-[#2A2A2A]/50 hover:bg-[#1a1a1a]" data-testid={`gsc-query-row-${r.query}`}>
              <td className="px-2 py-2 text-[#F5F1EB]">
                {r.query}
                {r.intent?.commercial && <span className="ml-2 text-[9px] text-[#C9A55A] uppercase">{t("gsc.intent.commercial")}</span>}
              </td>
              <td className="px-2 py-2 text-[#F5F1EB]">{r.clicks}</td>
              <td className="px-2 py-2 text-[#F5F1EB]">{r.impressions}</td>
              <td className="px-2 py-2 text-[#F5F1EB]">{r.ctr}%</td>
              <td className="px-2 py-2 text-[#F5F1EB]">{r.position}</td>
              <td className={`px-2 py-2 ${r.trend == null ? "text-[#8A8A8A]" : r.trend >= 0 ? "text-[#66d18a]" : "text-[#e57373]"}`}>
                {r.trend == null ? "—" : `${r.trend > 0 ? "+" : ""}${r.trend}%`}
              </td>
              <td className="px-2 py-2">
                <button
                  onClick={() => onSendToImprove(r)}
                  className="text-[10px] uppercase tracking-[0.16em] text-[#C9A55A] hover:opacity-80"
                  data-testid={`gsc-query-improve-${r.query}`}
                >
                  {t("gsc.opp.improve_btn")} →
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PagesTable({ rows, onSendToImprove }) {
  const { t } = useAdminT();
  const { sorted, sort, onSort } = useSortedRows(rows);
  return (
    <div className="border border-[#2A2A2A]">
      <table className="w-full">
        <thead className="border-b border-[#2A2A2A]">
          <tr className="text-left">
            <SortHeader label={t("gsc.table.page")} k="page" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.clicks")} k="clicks" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.impressions")} k="impressions" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.ctr")} k="ctr" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.position")} k="position" sort={sort} onSort={onSort} />
            <SortHeader label={t("gsc.table.trend")} k="trend" sort={sort} onSort={onSort} />
            <th />
          </tr>
        </thead>
        <tbody className="text-sm">
          {sorted.slice(0, 100).map((r) => (
            <tr key={r.page} className="border-b border-[#2A2A2A]/50 hover:bg-[#1a1a1a]">
              <td className="px-2 py-2 text-[#F5F1EB] max-w-[420px] truncate" title={r.page}>
                {r.guide?.titleEn ? (
                  <span>{r.guide.titleEn}<span className="text-[10px] text-[#8A8A8A] block truncate">{r.page}</span></span>
                ) : r.page}
              </td>
              <td className="px-2 py-2 text-[#F5F1EB]">{r.clicks}</td>
              <td className="px-2 py-2 text-[#F5F1EB]">{r.impressions}</td>
              <td className="px-2 py-2 text-[#F5F1EB]">{r.ctr}%</td>
              <td className="px-2 py-2 text-[#F5F1EB]">{r.position}</td>
              <td className={`px-2 py-2 ${r.trend == null ? "text-[#8A8A8A]" : r.trend >= 0 ? "text-[#66d18a]" : "text-[#e57373]"}`}>
                {r.trend == null ? "—" : `${r.trend > 0 ? "+" : ""}${r.trend}%`}
              </td>
              <td className="px-2 py-2">
                {r.guide && (
                  <button
                    onClick={() => onSendToImprove({ query: r.guide.titleEn, guideId: r.guide.id, page: r.page })}
                    className="text-[10px] uppercase tracking-[0.16em] text-[#C9A55A] hover:opacity-80"
                  >
                    Improve →
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OppCard({ opp, onImprove, onCreate }) {
  const { t } = useAdminT();
  return (
    <div className="border border-[#2A2A2A] p-3 space-y-1" data-testid={`gsc-opp-${opp.category || "generic"}-${opp.query || opp.page || ""}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[#F5F1EB]">{opp.query || opp.page}</div>
        {opp.tier && (
          <span className={`text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 ${TIER_COLOR[opp.tier] || TIER_COLOR.LOW}`}>
            {opp.tier.replace("_", " ")} · {opp.score}
          </span>
        )}
      </div>
      <div className="text-[11px] text-[#8A8A8A]">
        pos <span className="text-[#F5F1EB]">{opp.position}</span> ·
        impr <span className="text-[#F5F1EB]">{opp.impressions}</span> ·
        clicks <span className="text-[#F5F1EB]">{opp.clicks}</span>
        {opp.ctr != null && <> · ctr <span className="text-[#F5F1EB]">{opp.ctr}%</span></>}
        {opp.intent?.commercial && <span className="ml-2 text-[9px] text-[#C9A55A] uppercase">{t("gsc.intent.commercial")}</span>}
        {opp.intent?.local?.length > 0 && <span className="ml-1 text-[9px] text-[#66d18a] uppercase">{t("gsc.intent.local")}</span>}
      </div>
      {opp.guide && (
        <div className="text-[11px] text-[#8A8A8A]">
          {t("ai.field.rec_fleet")}: <span className="text-[#F5F1EB]">{opp.guide.titleEn}</span>
        </div>
      )}
      <div className="text-xs text-[#F5F1EB]">{opp.recommendation}</div>
      <div className="flex gap-2 pt-1">
        {opp.guide && (
          <button
            onClick={() => onImprove(opp)}
            className="text-[10px] uppercase tracking-[0.16em] text-[#C9A55A] border border-[#C9A55A]/50 px-2 py-1 hover:bg-[#C9A55A] hover:text-[#0C0C0C]"
          >
            {t("gsc.opp.improve_btn")}
          </button>
        )}
        <button
          onClick={() => onCreate(opp)}
          className="text-[10px] uppercase tracking-[0.16em] text-[#F5F1EB] border border-[#2A2A2A] px-2 py-1 hover:border-[#C9A55A]"
        >
          {t("gsc.opp.create_btn")}
        </button>
      </div>
    </div>
  );
}

export default function GscPanel({ onSendToImprove, onSendToCreate }) {
  const { t, lang } = useAdminT();
  const [status, setStatus] = useState(null);
  const [range, setRange] = useState("28d");
  const [perf, setPerf] = useState(null);
  const [queries, setQueries] = useState([]);
  const [pages, setPages] = useState([]);
  const [opps, setOpps] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [advising, setAdvising] = useState(false);
  const [propertyPicker, setPropertyPicker] = useState(false);
  const [propertyOptions, setPropertyOptions] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ai-seo/gsc/status");
      setStatus(data);
    } catch { /* ignore */ }
  }, []);

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [p, q, pg, o, a] = await Promise.allSettled([
        api.get(`/admin/ai-seo/gsc/performance?range=${range}`),
        api.get(`/admin/ai-seo/gsc/queries?range=${range}&limit=100`),
        api.get(`/admin/ai-seo/gsc/pages?range=${range}&limit=100`),
        api.get(`/admin/ai-seo/gsc/opportunities?range=${range}`),
        api.get(`/admin/ai-seo/gsc/advice/latest`),
      ]);
      if (p.status === "fulfilled") setPerf(p.value.data);
      if (q.status === "fulfilled") setQueries(q.value.data.rows || []);
      if (pg.status === "fulfilled") setPages(pg.value.data.rows || []);
      if (o.status === "fulfilled") setOpps(o.value.data);
      if (a.status === "fulfilled") setAdvice(a.value.data);
    } finally {
      setRefreshing(false);
    }
  }, [range]);

  useEffect(() => { (async () => { await loadStatus(); setLoading(false); })(); }, [loadStatus]);
  useEffect(() => {
    if (status?.connected && status?.property) loadAll();
  }, [status?.connected, status?.property, range, loadAll]);

  const connect = async () => {
    try {
      const { data } = await api.get("/admin/ai-seo/gsc/auth-url");
      window.open(data.url, "_blank", "noopener");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to start Google auth");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Search Console?")) return;
    await api.post("/admin/ai-seo/gsc/disconnect");
    setPerf(null); setQueries([]); setPages([]); setOpps(null); setAdvice(null);
    await loadStatus();
  };

  const pickProperty = async () => {
    try {
      const { data } = await api.get("/admin/ai-seo/gsc/properties");
      setPropertyOptions(data.properties || []);
      setPropertyPicker(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to load properties");
    }
  };

  const selectProperty = async (siteUrl) => {
    await api.post("/admin/ai-seo/gsc/property", { property: siteUrl });
    setPropertyPicker(false);
    await loadStatus();
  };

  const runAdvise = async () => {
    setAdvising(true);
    try {
      const { data } = await api.post("/admin/ai-seo/gsc/advise", { range, lang });
      setAdvice(data);
    } catch (e) {
      alert(e.response?.data?.detail || "Advisor failed");
    } finally {
      setAdvising(false);
    }
  };

  if (loading) return <div className="text-[#8A8A8A]">{t("common.loading")}</div>;

  const configured = !!status?.configured;
  const connected = !!status?.connected;

  return (
    <div data-testid="gsc-panel">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl text-[#F5F1EB] flex items-center gap-2">
            <Target size={20} className="text-[#C9A55A]" /> {t("gsc.title")}
          </h2>
          <p className="text-xs text-[#8A8A8A] mt-1">
            {t("gsc.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {connected ? (
            <>
              <span className="text-[10px] text-[#66d18a] uppercase tracking-[0.18em] border border-[#66d18a] px-2 py-1" data-testid="gsc-status-connected">
                <CheckCircle2 size={10} className="inline mr-1" /> {t("gsc.connected")}
              </span>
              {status.property ? (
                <span className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.16em]" data-testid="gsc-current-property">
                  {status.property}
                </span>
              ) : (
                <button onClick={pickProperty} className="text-[10px] text-[#C9A55A] underline uppercase tracking-[0.16em]" data-testid="gsc-pick-property">
                  {t("gsc.pick_property")}
                </button>
              )}
              <button
                onClick={pickProperty}
                className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] hover:text-[#F5F1EB] border border-[#2A2A2A] px-2 py-1"
                data-testid="gsc-change-property"
              >
                {t("gsc.change_property")}
              </button>
              <button
                onClick={loadAll}
                disabled={refreshing}
                className="text-[10px] uppercase tracking-[0.16em] text-[#C9A55A] border border-[#C9A55A]/50 px-2 py-1 hover:bg-[#C9A55A] hover:text-[#0C0C0C]"
                data-testid="gsc-refresh"
              >
                {refreshing ? <Loader2 size={10} className="inline animate-spin" /> : <RefreshCw size={10} className="inline" />} {t("common.refresh")}
              </button>
              <button
                onClick={disconnect}
                className="text-[10px] uppercase tracking-[0.16em] text-[#e57373] hover:opacity-80"
                data-testid="gsc-disconnect"
              >
                <LogOut size={10} className="inline mr-1" /> {t("gsc.disconnect")}
              </button>
            </>
          ) : (
            <span className="text-[10px] text-[#e57373] uppercase tracking-[0.18em] border border-[#e57373] px-2 py-1" data-testid="gsc-status-disconnected">
              {t("gsc.not_connected")}
            </span>
          )}
        </div>
      </div>

      {/* Property picker modal-ish */}
      {propertyPicker && (
        <div className="border border-[#C9A55A] p-4 mb-6" data-testid="gsc-property-picker">
          <div className="text-overline text-[#C9A55A] mb-3">{t("gsc.select_property_heading")}</div>
          {propertyOptions.length === 0 ? (
            <div className="text-sm text-[#8A8A8A]">No Search Console properties available for this Google account. Verify the site at <a className="text-[#C9A55A] underline" href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">Search Console</a> first.</div>
          ) : (
            <div className="space-y-1">
              {propertyOptions.map((p) => (
                <button
                  key={p.siteUrl}
                  onClick={() => selectProperty(p.siteUrl)}
                  className="w-full text-left text-sm text-[#F5F1EB] hover:bg-[#1a1a1a] p-2 border border-[#2A2A2A]"
                  data-testid={`gsc-property-option-${p.siteUrl}`}
                >
                  {p.siteUrl} <span className="text-[10px] text-[#8A8A8A] ml-2">{p.permissionLevel}</span>
                </button>
              ))}
            </div>
          )}
          <button onClick={() => setPropertyPicker(false)} className="mt-3 text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A]">{t("common.close")}</button>
        </div>
      )}

      {/* Body */}
      {!connected ? (
        <EmptyGsc onConnect={connect} configured={configured} redirectUri={window.location.origin + "/api/admin/ai-seo/gsc/callback"} />
      ) : !status.property ? (
        <div className="border border-dashed border-[#C9A55A] p-6 text-center">
          <p className="text-sm text-[#F5F1EB] mb-3">{t("gsc.select_property_hint")}</p>
          <button onClick={pickProperty} className="btn-gold" data-testid="gsc-pick-property-cta">{t("gsc.pick_property")}</button>
        </div>
      ) : (
        <>
          {/* Range selector */}
          <div className="flex flex-wrap gap-1 mb-4" data-testid="gsc-range-tabs">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={`px-3 h-8 text-[10px] uppercase tracking-[0.18em] border ${range === r.key ? "border-[#C9A55A] text-[#C9A55A]" : "border-[#2A2A2A] text-[#8A8A8A] hover:text-[#F5F1EB]"}`}
                data-testid={`gsc-range-${r.key}`}
              >
                {t(r.labelKey)}
              </button>
            ))}
            {status.lastSyncAt && (
              <span className="text-[10px] text-[#8A8A8A] uppercase tracking-[0.16em] ml-2 self-center">
                {t("gsc.last_sync", { when: new Date(status.lastSyncAt).toLocaleString() })}
              </span>
            )}
          </div>

          {/* KPIs */}
          {perf ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="gsc-kpis">
              <Kpi label={t("gsc.kpi.clicks")} value={perf.current.clicks.toLocaleString()} delta={perf.delta.clicksPct} unit="%" />
              <Kpi label={t("gsc.kpi.impressions")} value={perf.current.impressions.toLocaleString()} delta={perf.delta.impressionsPct} unit="%" />
              <Kpi label={t("gsc.kpi.ctr")} value={perf.current.ctr.toFixed(2)} suffix="%" delta={perf.delta.ctrDeltaPP} unit="pp" />
              <Kpi label={t("gsc.kpi.position")} value={perf.current.position.toFixed(2)} delta={perf.delta.positionDelta} invert unit="pos" />
            </div>
          ) : (
            <div className="text-[#8A8A8A] text-sm mb-6">{t("common.loading")}</div>
          )}

          {perf && perf.current.impressions < 10 && (
            <div className="border border-[#4a3315] bg-[#1a0d0d] text-[#e57373] p-3 text-xs mb-6">
              <AlertTriangle size={12} className="inline mr-1" />
              {t("gsc.not_enough_data")}
            </div>
          )}

          {/* AI Advisor */}
          <div className="border border-[#2A2A2A] p-4 mb-6" data-testid="gsc-advisor">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-overline text-[#C9A55A] flex items-center gap-2"><Brain size={14} /> {t("gsc.advisor.title")}</h3>
              <button onClick={runAdvise} disabled={advising} className="btn-gold text-[10px]" data-testid="gsc-run-advise">
                {advising ? <Loader2 size={10} className="inline animate-spin mr-1" /> : <Sparkles size={10} className="inline mr-1" />}
                {advising ? t("gsc.advisor.running") : t("gsc.advisor.run")}
              </button>
            </div>
            {advice?.priorities?.length ? (
              <div className="space-y-2 mt-3">
                {advice.priorities.slice(0, 5).map((p, i) => (
                  <div key={i} className="border-l-2 border-[#C9A55A] pl-3">
                    <div className="text-sm text-[#F5F1EB]">{p.title}</div>
                    <div className="text-[11px] text-[#8A8A8A] mt-1">{t("gsc.advisor.reason")}: {p.reason}</div>
                    <div className="text-[11px] text-[#8A8A8A]">{t("gsc.advisor.evidence")}: {p.evidence}</div>
                    <div className="text-[11px] text-[#F5F1EB]">{t("gsc.advisor.action")}: {p.action}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#C9A55A] mt-1">
                      {t("gsc.advisor.impact")}: {p.expectedImpact} · {t("gsc.advisor.effort")}: {p.effort}
                    </div>
                  </div>
                ))}
                {advice.createdAt && (
                  <div className="text-[10px] text-[#8A8A8A] mt-2">{t("gsc.advisor.analyzed_at", { when: new Date(advice.createdAt).toLocaleString() })}</div>
                )}
              </div>
            ) : (
              <div className="text-[11px] text-[#8A8A8A]">{t("gsc.advisor.explainer")}</div>
            )}
          </div>

          {/* Opportunities */}
          {opps && (
            <>
              <h3 className="text-overline text-[#C9A55A] mt-8 mb-3 flex items-center gap-2"><Zap size={14} /> {t("gsc.opp.quick_wins")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="gsc-quick-wins">
                {(opps.quickWins || []).map((o, i) => (
                  <OppCard key={i} opp={o} onImprove={onSendToImprove} onCreate={onSendToCreate} />
                ))}
                {(opps.quickWins || []).length === 0 && (
                  <div className="text-[#8A8A8A] text-sm">{t("gsc.opp.no_quick_wins")}</div>
                )}
              </div>

              <h3 className="text-overline text-[#C9A55A] mt-8 mb-3 flex items-center gap-2"><Target size={14} /> {t("gsc.opp.striking")} ({opps.counts.strikingDistance})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="gsc-striking">
                {(opps.strikingDistance || []).slice(0, 10).map((o, i) => (
                  <OppCard key={i} opp={o} onImprove={onSendToImprove} onCreate={onSendToCreate} />
                ))}
              </div>

              <h3 className="text-overline text-[#C9A55A] mt-8 mb-3">{t("gsc.opp.low_ctr")} ({opps.counts.highImpressionsLowCtr})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="gsc-low-ctr">
                {(opps.highImpressionsLowCtr || []).slice(0, 10).map((o, i) => (
                  <OppCard key={i} opp={o} onImprove={onSendToImprove} onCreate={onSendToCreate} />
                ))}
              </div>

              <h3 className="text-overline text-[#C9A55A] mt-8 mb-3 flex items-center gap-2"><Layers size={14} /> {t("gsc.opp.content_gap")} ({opps.counts.contentGap})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="gsc-content-gap">
                {(opps.contentGap || []).slice(0, 10).map((o, i) => (
                  <OppCard key={i} opp={o} onImprove={onSendToImprove} onCreate={onSendToCreate} />
                ))}
              </div>

              <h3 className="text-overline text-[#C9A55A] mt-8 mb-3">{t("gsc.opp.cannibalization")} ({opps.counts.cannibalization})</h3>
              <div className="space-y-2 mb-6" data-testid="gsc-cannibalization">
                {(opps.cannibalization || []).map((c, i) => (
                  <div key={i} className="border border-[#2A2A2A] p-3">
                    <div className="text-[#F5F1EB]">{c.query}</div>
                    <div className="text-[11px] text-[#8A8A8A] mt-1">
                      {c.competingPages.length} pages · total impr {c.totalImpressions} · clicks {c.totalClicks}
                    </div>
                    <div className="mt-2 text-[11px] text-[#F5F1EB]">
                      {c.competingPages.map((p, j) => (
                        <div key={j} className="truncate">→ pos {p.position} · {p.impressions} impr · {p.page}</div>
                      ))}
                    </div>
                    <div className="text-[11px] text-[#C9A55A] mt-1">{c.recommendation}</div>
                  </div>
                ))}
                {(opps.cannibalization || []).length === 0 && (
                  <div className="text-[#8A8A8A] text-sm">{t("gsc.opp.no_cannibalization")}</div>
                )}
              </div>

              <h3 className="text-overline text-[#C9A55A] mt-8 mb-3">{t("gsc.opp.declining")} ({opps.counts.declining})</h3>
              <div className="space-y-2 mb-6" data-testid="gsc-declining">
                {(opps.declining || []).map((d, i) => (
                  <div key={i} className="border border-[#2A2A2A] p-3">
                    <div className="text-[#F5F1EB] truncate">{d.guide?.titleEn || d.page}</div>
                    <div className="text-[11px] text-[#8A8A8A] truncate">{d.page}</div>
                    <div className="text-[11px] text-[#F5F1EB] mt-1">
                      Clicks {d.clicksPrev} → {d.clicksNow}
                      <span className={`ml-2 ${d.clicksPct < 0 ? "text-[#e57373]" : "text-[#66d18a]"}`}>
                        ({d.clicksPct > 0 ? "+" : ""}{d.clicksPct}%)
                      </span>
                      · pos {d.positionPrev} → {d.positionNow} ({d.positionDelta > 0 ? "+" : ""}{d.positionDelta})
                    </div>
                    <div className="text-[11px] text-[#C9A55A] mt-1">{d.recommendation}</div>
                    {d.guide && (
                      <button
                        onClick={() => onSendToImprove({ query: d.guide.titleEn, guideId: d.guide.id, page: d.page })}
                        className="text-[10px] uppercase tracking-[0.16em] text-[#C9A55A] border border-[#C9A55A]/50 px-2 py-1 mt-2 hover:bg-[#C9A55A] hover:text-[#0C0C0C]"
                      >
                        {t("gsc.opp.analyze_btn")}
                      </button>
                    )}
                  </div>
                ))}
                {(opps.declining || []).length === 0 && (
                  <div className="text-[#8A8A8A] text-sm">{t("gsc.opp.no_declining")}</div>
                )}
              </div>
            </>
          )}

          {/* Tables */}
          <h3 className="text-overline text-[#C9A55A] mt-8 mb-3">{t("gsc.table.top_queries")}</h3>
          <QueriesTable rows={queries} onSendToImprove={onSendToImprove} />

          <h3 className="text-overline text-[#C9A55A] mt-8 mb-3">{t("gsc.table.top_pages")}</h3>
          <PagesTable rows={pages} onSendToImprove={onSendToImprove} />
        </>
      )}
    </div>
  );
}
